import * as Sentry from '@sentry/react';

import Context from '../configuration/context';
import { ITokenProvider } from '../services/authenticationService';
import { InvoiceDetails, SsoInfo } from './contracts';

const MethodPost = 'POST';
const MethodGet = 'GET';
const ProblemTypeBase = 'https://portal-api.avonova.se/problem/';

class AuthApiClient {
  constructor(
    private readonly baseApiUrl: string,
    private readonly accessToken?: string,
    private readonly tokenProvider?: ITokenProvider
  ) {}

  async getCustomerFile(customerId: string, fileId: number): Promise<Blob> {
    const url = `${this.baseApiUrl}/v1/customer/${customerId}/files/${fileId}`;

    const response = await this.makeRequest('get', url);

    return await response.blob();
  }

  async resetMfa(body: ResetMfaBody): Promise<void> {
    const url = `${this.baseApiUrl}/v1/user/mfa/reset`;
    await this.makeRequest('post', url, body);
  }

  async getInvoice(invoiceId: string, customerId: string): Promise<InvoiceDetails> {
    const url = `${this.baseApiUrl}/v1/invoices/${invoiceId}?customerId=${customerId}`;
    const response = await this.makeRequest('get', url);
    return (await response.json()) as InvoiceDetails;
  }

  private async makeRequest(method: string, url: string, data?: any): Promise<Response> {
    const accessToken =
      this.accessToken ?? (await this.tokenProvider?.getOrRenewNativeToken()) ?? '';

    const options: RequestInit = {
      method,
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    };

    if (data) {
      options.headers = {
        ...options.headers,
        'Content-Type': 'application/json',
      };
      options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);
    if (!response.ok) {
      const responseText = await response.text();

      Sentry.captureEvent({
        level: 'error',
        message: 'Api request failed: ' + url,
        extra: {
          url,
          method,
          data,
          status: response.status,
          responseText,
        },
      });

      throw new ApiError(response.status, responseText);
    }

    return response;
  }
}

class ApiClient {
  constructor(private readonly baseApiUrl: string) {}

  getToken(req: TokenRequest): Promise<GetTokenResult> {
    return this.makeRequest<GetTokenResult>(MethodPost, `${this.baseApiUrl}/auth/token`, req);
  }

  getSsoDetails(id: string): Promise<SsoInfo> {
    return this.makeRequest<SsoInfo>(MethodGet, `${this.baseApiUrl}/auth/sso/${id}`);
  }

  withAuth(tokenProvider: ITokenProvider): AuthApiClient {
    return new AuthApiClient(this.baseApiUrl, undefined, tokenProvider);
  }

  withAccessToken(accessToken: string): AuthApiClient {
    return new AuthApiClient(this.baseApiUrl, accessToken, undefined);
  }

  private async makeRequest<T>(method: string, url: string, data?: any): Promise<T> {
    const options: RequestInit = {
      method,
    };

    if (data) {
      options.headers = {
        'Content-Type': 'application/json',
      };
      options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);
    if (!response.ok) {
      const responseText = await response.text();

      Sentry.captureEvent({
        level: 'error',
        message: 'Api request failed: ' + url,
        extra: {
          url,
          method,
          data,
          status: response.status,
          responseText,
        },
      });

      throw new ApiError(response.status, responseText);
    }

    return (await response.json()) as Promise<T>;
  }
}

export interface TokenRequest {
  email?: string;
  password?: string;
  sessionId?: string;
  code?: string;
  ssoIdToken?: string;
  ssoAccessToken?: string;
}

export interface GetTokenResultMfa {
  sessionId: string;
  setupUri: string;
}

export interface GetTokenResultToken {
  accessToken: string;
  refreshToken: string;
}

export interface GetTokenResult {
  mfa?: GetTokenResultMfa;
  token?: GetTokenResultToken;
}

export interface ResetMfaBody {
  password: string;
  mfaCode: string;
}

export class ApiError {
  private readonly _problem?: AvonovaProblem;

  constructor(
    private readonly _status: number,
    _responseText: string
  ) {
    try {
      this._problem = JSON.parse(_responseText) as AvonovaProblem;
      this._problem.typeName = this._problem.type.replace(ProblemTypeBase, '');
    } catch {}
  }

  get status(): number {
    return this._status;
  }

  get problem(): AvonovaProblem | undefined {
    return this._problem;
  }
}

export type AvonovaProblem = {
  type: string;
  typeName: string;
  data?: any;
};

export const AvonovaProblemTypes = {
  Unauthorized: 'Unauthorized',
};

const apiClient = new ApiClient(Context.APIUrl);
export default apiClient;
